import React, {
  FC,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
  useReducer,
  CSSProperties,
  FocusEvent,
} from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
} from "@headlessui/react";

import classNames from "classnames";
import { produce } from "immer";
import axios, { CancelTokenSource } from "axios";
import { useDispatch } from "react-redux";
import { RSQLFilterExpression, Operators, RSQLFilterList } from "rsql-criteria-typescript";

import { Pojo } from "types/Galaxy";
import { findOne, findAll } from "api";
import { Fa } from "composants/Icon";
import {
  getSizeClasses,
  getColorsClasses,
  ComposantAllProps,
  ParentComponentProps,
} from "composants/common";

import Control, { ControlProps } from "composants/form/Control";

import { initRsqlCriteria, GS, GSBuilder } from "utils/query.utils";

import { contextualize } from "actions/contextTreeModule";
import { ButtonNoStyle } from "composants/button";
import { Action } from "redux";
import { ActionTypeData } from "reducers/Action";
import { useTranslation } from "react-i18next";
import { GensearchSortClause } from "types/Component";

import "./Autocomplete.css";
import { uuidv4 } from "utils/uuid.utils";
import useInteractions from "hooks/useInteractions";
import toaster from "composants/toaster/toaster";
import { NotificationGroup } from "types/Notification";

import {
  getAutocompleteCache,
  getCacheKey,
  hasAutocompleteCache,
  isValidCache,
  setAutocompleteCache,
  registerAutocompleteCallback,
} from "./autocomplete-cache";
import { Tooltip } from "@axin-org/comet";
import { highlightTerm } from "./autocomplete.utils";

const PAGE_SIZE = 50;

function isNullish(value: any) {
  return value === null || value === undefined;
}

export interface AutocompleteProps extends ComposantAllProps {
  autoFocus?: boolean;
  sjmoCode: string;
  tableName?: string;
  joinTableName?: string;
  joinListFields?: string;
  additionalClause?: GS;
  sortClause?: GensearchSortClause[];
  controlProps?: ControlProps;
  placeholder?: string;
  "data-navigation-key"?: string;
  "data-navigation-row"?: number;
  "data-navigation-column"?: number;
  parent?: ParentComponentProps;
  style?: CSSProperties;
  styleInput?: CSSProperties;
  includeOelStyle?: boolean;
  options?: Record<string, any>;
  hasLov?: boolean;
  autocomplete?: "off";
  forceInteractionOff?: boolean;
  onItemChange(itemChange: Pojo | null, name: string | undefined): void;
  onFocus?(event: FocusEvent<HTMLInputElement>): void;
  onChangeTab?(itemChange: Pojo | null, name: string | undefined): void;
  onEnterSelect?(itemChange: Pojo | null, name: string | undefined): void;
}

const Autocomplete: FC<
  AutocompleteProps & {
    openOnFocus?: boolean;
    lefIcon?: React.ReactNode;
  }
> = (props) => {
  const [t] = useTranslation();
  const cacheKey = getCacheKey(props.joinTableName, props.value);
  const [state, setState] = useState<"ON_MOUNT" | "IDLE">("ON_MOUNT");
  const [currentEntity, setCurrentEntity] = useState<Pojo | null>(getAutocompleteCache(cacheKey));
  const [list, dispatch] = useReducer(reducerData, []);
  const [isLoading, setIsLoading] = useState(false);
  const [term, setTerm] = useState("");
  const [page, setPage] = useState(0);
  const reduxDispatch = useDispatch();
  const optionClickedRef = useRef(false);
  const buttonClickedRef = useRef(false);
  const tabPressedRef = useRef(false);
  const enterPressedRef = useRef(false);
  const enterSelectedRef = useRef(false);
  const popOverRef = useRef<HTMLElement | null>(null);
  const fetchTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const fetchCancelRef = useRef<CancelTokenSource | undefined>(undefined);

  // Les navigateurs propose tous seul de l'autocomplete sur les fields
  // Pour ignorer ça on devrait mettre [autocomplete = "off"]
  // Mais les navigateurs ignore cette dernière depuis peu
  // Alors on truande le système de la manière suivante
  // --------------------------------------------------
  // Lorsqu'un name est présent dans l'input,
  // il est préférable d'utiliser un uuid
  // Si on a pas de name a positionner
  // il vaut mieux mettre "off"
  const autocompleteKiller = useMemo(() => {
    if (props.autocomplete) {
      return props.autocomplete;
    } else {
      return `${props.id}-${new Date().toISOString()}`;
    }
  }, [props]);
  // On refresh sur props en entier afin que plusieurs recherches succéssives, sans refresh du composant,
  // ne déclenchent pas l'autocomplete de google

  let shouldAddInteraction = false;

  const interactionsGS = useInteractions({
    type: "dataPreferred",
    sjmoCode: props.sjmoCode,
    ctrlKey: (props.name || props.id) ?? "<unknown>",
    tableName: props.tableName,
  });

  const loadSelectedEntity = useCallback(
    function loadSelectedEntity(source?: CancelTokenSource) {
      const cacheKey = getCacheKey(props.joinTableName, props.value ? props.value : null);

      if (hasAutocompleteCache(cacheKey)) {
        setCurrentEntity(getAutocompleteCache(cacheKey));
      }

      if (!isValidCache(cacheKey)) {
        const interactions = props.forceInteractionOff !== true ? interactionsGS : undefined;

        let rsql = initRsqlCriteria();
        if (interactions && shouldAddInteraction) {
          const keys = Object.keys(interactions);
          for (let key of keys) {
            rsql.filters.and(new RSQLFilterExpression(key, Operators.Like, interactions[key]));
          }
        }

        if (props.joinTableName && !isNullish(props.value)) {
          findOne(
            {
              tableName: props.joinTableName,
              id: props.value,
              includeStyle: props.includeOelStyle,
              labelDetailsContext: props.joinListFields,
              sjmoCode: props.sjmoCode,
            },
            source,
          )
            .then((res) => {
              setAutocompleteCache(cacheKey, res.data);
              setCurrentEntity(res.data);
              setTerm(res.data.label);
            })
            .catch((e) => {
              if (!axios.isCancel(e)) {
                console.error("error during the fetch of the entity with id=", props.value);
                console.error(e);
              }
            });
        } else if (isNullish(props.value)) {
          setCurrentEntity(null);
          setTerm("");
        }
      }

      setState("IDLE");
    },
    [
      interactionsGS,
      props.forceInteractionOff,
      props.includeOelStyle,
      props.joinListFields,
      props.joinTableName,
      props.sjmoCode,
      props.value,
      shouldAddInteraction,
    ],
  );

  useEffect(() => {
    const source = axios.CancelToken.source();
    loadSelectedEntity(source);
    return () => {
      source.cancel();
    };
  }, [loadSelectedEntity]);

  const contextualizeCtrlKey = props.name ?? props.id ?? "<unknown>";
  useEffect(() => {
    const cancelTasks = reduxDispatch(
      contextualize(
        props.sjmoCode,
        props.tableName || null,
        contextualizeCtrlKey,
        currentEntity !== null ? currentEntity : null,
      ),
    ) as any;

    return () => {
      cancelTasks();
    };
  }, [currentEntity, reduxDispatch, contextualizeCtrlKey, props.sjmoCode, props.tableName]);

  useEffect(() => {
    function callback() {
      loadSelectedEntity();
    }

    const cleanup = registerAutocompleteCallback(cacheKey, callback);

    return () => {
      cleanup();
    };
  }, [cacheKey, loadSelectedEntity]);

  const fetchData = useCallback(
    async function fetchData(
      term?: string,
      reset = true,
      first = 0,
      size = PAGE_SIZE,
      cancelToken?: CancelTokenSource,
    ) {
      if (reset) {
        setPage(0);
      }

      const interactions = props.forceInteractionOff !== true ? interactionsGS : undefined;
      if (!(props.additionalClause && props.joinTableName)) {
        return Promise.resolve();
      }
      setIsLoading(true);
      let rsqlFilter: RSQLFilterList | null = null;
      if (term) {
        // on transforme la requête en RSQL avec les valeurs de l'input.
        rsqlFilter = GSBuilder.toFilter(props.additionalClause, term);
      } else {
        // on garde seulement les critères suivant pour un find ALL:
        // - OPER_NULL
        // - OPER_NOT_NULL
        // - les critères de recherche avec des valeurs prédéfinies
        const filteredNull = GSBuilder.visit<GS>(props.additionalClause, {
          And: (node, visitor) => {
            let result: GS[] = [];
            for (let n of node.nodes) {
              let visitedNode = GSBuilder.visit(n, visitor);
              if (visitedNode) result.push(visitedNode);
            }
            return GSBuilder.AND(...result);
          },
          Or: (node, visitor) => {
            let result: GS[] = [];
            for (let n of node.nodes) {
              let visitedNode = GSBuilder.visit(n, visitor);
              if (visitedNode) result.push(visitedNode);
            }
            return GSBuilder.OR(...result);
          },
          Comparison: (node) => {
            if (
              node.operator === "OPER_NULL" ||
              node.operator === "OPER_NOT_NULL" ||
              (node.value !== undefined && node.value !== null && node.value !== "")
            )
              return node;
            return null as any;
          },
        });
        if (filteredNull) rsqlFilter = GSBuilder.toFilter(filteredNull);
      }

      const rsql = initRsqlCriteria();
      if (interactions) {
        const keys = Object.keys(interactions);

        for (let key of keys) {
          rsql.filters.and(new RSQLFilterExpression(key, Operators.Like, interactions[key]));
        }
      }
      if (rsqlFilter !== null) rsql.filters.and(rsqlFilter);

      if (props.sortClause) {
        for (let s of props.sortClause) {
          if (s.sortOrder !== "UNSORTED") {
            rsql.orderBy.add(s.sortField, s.sortOrder === "ASCENDING" ? "asc" : "desc");
          }
        }
      }

      try {
        const res = await findAll(
          {
            tableName: props.joinTableName,
            sjmoCode: props.sjmoCode,
            labelDetailsContext: props.joinListFields,
            filter: rsql ? rsql.build() + "&" : "",
            first,
            size,
          },
          cancelToken,
        );
        if (reset) {
          const results = res.data.data;
          // Si un item correspond exactement au terme saisi, on le place en première position
          // afin que headlessui le focus par défaut — TAB sélectionne alors la correspondance exacte.
          const exactIdx = results.findIndex(
            (el) => el && (el.label === term || el.labelDetails === term),
          );
          if (exactIdx > 0) {
            results.unshift(results.splice(exactIdx, 1)[0]);
          }
          dispatch({ type: "REPLACE", payload: results });
        } else {
          dispatch({ type: "APPEND", payload: { first, size, pojos: res.data.data } });
        }
      } catch (e) {
        if (!axios.isCancel(e)) {
          dispatch({ type: "EMPTY" });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      interactionsGS,
      props.additionalClause,
      props.forceInteractionOff,
      props.joinListFields,
      props.joinTableName,
      props.sjmoCode,
      props.sortClause,
    ],
  );

  useLayoutEffect(() => {
    setTerm(currentEntity != null ? currentEntity.label : "");
  }, [currentEntity]);

  function onBlur(e: React.FocusEvent<HTMLInputElement>) {
    let value = e.currentTarget.value;

    if (value.startsWith("<") || value.startsWith(">")) {
      return;
    }

    requestAnimationFrame(() => {
      if (optionClickedRef.current === true) {
        optionClickedRef.current = false;
        return;
      }

      if (buttonClickedRef.current === true) {
        buttonClickedRef.current = false;
        return;
      }

      // Une sélection Enter a déjà committé la valeur via onEnterSelect — on court-circuite le findOne.
      if (enterSelectedRef.current === true) {
        enterSelectedRef.current = false;
        return;
      }

      if (value === "" && props.value && currentEntity) {
        setTerm(currentEntity.label);
      } else if (
        props.joinTableName &&
        (currentEntity === null || (currentEntity !== null && currentEntity.label !== term))
      ) {
        if (term === "") {
          setCurrentEntity(null);
          if (props.value !== null) {
            props.onItemChange(null, props.name);
          }
        } else {
          findOne({
            tableName: props.joinTableName,
            id: value,
            sjmoCode: props.sjmoCode,
          })
            .then((res) => {
              setAutocompleteCache(getCacheKey(props.joinTableName, res.data.id), res.data);
              setCurrentEntity(res.data);
              const id = res.data?.id;
              if (id !== props.value) {
                props.onItemChange(res.data, props.name);
                props.onChangeTab?.(res.data, props.name);
              }
            })
            .catch((e) => {
              if (currentEntity != null) {
                setTerm(currentEntity.label);
              } else {
                setTerm("");
                setCurrentEntity(null);
              }
              console.error("error during the auto fetch of the entity with id=", value);
              console.error(e);
            });
        }
      }
    });
    if (props.onBlur) {
      props.onBlur(e);
    }
  }

  useEffect(() => {
    return () => {
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
      fetchCancelRef.current?.cancel();
    };
  }, []);

  function onSelectItem(label: string) {
    // Sur TAB, headlessui appelle selectActiveOption() qui fire onChange.
    // On ignore cette sélection automatique : onBlur fera findOne(value) pour un lookup exact côté backend,
    // indépendamment de l'état de la liste (qui peut être périmée si l'utilisateur a tapé vite).
    if (tabPressedRef.current) {
      tabPressedRef.current = false;
      return;
    }

    enterPressedRef.current = false;

    const item = list.find((el) => el && (el.labelDetails === label || el.label === label));
    // si on a undefined, on a un BUG
    if (item !== undefined) {
      // optionClickedRef est positionné dans onMouseDown (avant que HeadlessUI fire onChange),
      // ce qui le rend fiable pour distinguer un clic d'une sélection clavier (Enter).
      const isEnterSelection = !optionClickedRef.current;
      // Bloque le findOne déclenché par onBlur, qui écraserait sinon la sélection avec le terme tapé.
      if (isEnterSelection) enterSelectedRef.current = true;
      setCurrentEntity(item);
      props.onItemChange(item, props.name);
      // Permet au consommateur (ex: cell editor AG-Grid) de committer la valeur et fermer l'éditeur.
      if (isEnterSelection) props.onEnterSelect?.(item, props.name);
    }
  }

  function onFocus(e: React.FocusEvent<HTMLInputElement>) {
    props.onFocus && props.onFocus(e);
    if (!e.isDefaultPrevented() && props.openOnFocus) {
      fetchData();
    }
  }

  function onChange(e: React.FormEvent<HTMLInputElement>) {
    let value = e.currentTarget.value;
    if (term === "") value = value.trim();
    setTerm(value);
    if (value.length > 0 && props.joinTableName) {
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
      fetchCancelRef.current?.cancel();
      const cancel = axios.CancelToken.source();
      fetchCancelRef.current = cancel;
      fetchTimerRef.current = setTimeout(() => {
        fetchData(value, true, 0, PAGE_SIZE, cancel);
      }, 200);
    }
  }

  function onClose() {
    if (!(props.readOnly || props.disabled)) {
      setCurrentEntity(null);
      props.onItemChange(null, props.name);
    }
  }

  function toggleSearch() {
    if (!(props.readOnly || props.disabled)) {
      fetchData();
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    props.onKeyDown && props.onKeyDown(e);

    if (!e.isDefaultPrevented() && e.key === "ArrowDown") {
      fetchData(term);
    }

    if (e.key === "Tab") {
      tabPressedRef.current = true;
    }

    if (e.key === "Enter") {
      enterPressedRef.current = true;
    }

    ajustScrollPopover();
  }

  function ajustScrollPopover() {
    let container = popOverRef.current;
    if (!container) return;

    const element = container.querySelector<HTMLElement>("[aria-selected=true]");

    // on set le scroll en fonction de l'offsetTop de l'élément que l'on a et de l'offset top de son conteneur.
    // ce qui nous permet de suivre l'élément au fur et à mesure que l'élément sélectionné (via `aria-selected`)
    // change de position (haut / bas).
    if (element) container.scrollTop = element.offsetTop - container.offsetTop;
  }

  function onDoubleClick() {
    if (
      props.parent &&
      props.parent.ctrlKey &&
      props.joinTableName &&
      props.id &&
      !props.readOnly &&
      props.hasLov
    ) {
      // Désactivé tant que le ticket #1250 n'est pas fait.
      toaster.notify({
        id: uuidv4(),
        group: NotificationGroup.DEFAULT,
        title: "LOV unitaire désactivé",
        createdAt: new Date().toISOString(),
        intent: "INFO",
        priority: "NORMAL",
      });
    }
  }

  let termIsLabelEntity =
    currentEntity && (term === currentEntity.labelDetails || term === currentEntity.label);
  useEffect(() => {
    if (page >= 1 && page * PAGE_SIZE >= list.length) {
      fetchData(termIsLabelEntity ? undefined : term, false, page * PAGE_SIZE, PAGE_SIZE);
    }
  }, [fetchData, page, term, list, termIsLabelEntity]);

  if (state === "ON_MOUNT") {
    return null;
  }

  return (
    <>
      <Combobox
        as="div"
        className="field has-addons"
        style={props.style}
        value={term}
        onChange={onSelectItem}
      >
        <Control
          className="control"
          iconsLeft={
            (currentEntity?.alerte || props.lefIcon) && (
              <>
                {currentEntity?.alerte && (
                  <Tooltip
                    label={<div dangerouslySetInnerHTML={{ __html: currentEntity?.alerte }} />}
                    portalZindex={500}
                  >
                    <ButtonNoStyle
                      className={classNames(
                        "icon is-small is-left cursor-pointer",
                        props.lefIcon ? "ml-7" : "",
                      )}
                      style={{ zIndex: 5 }}
                    >
                      <Fa icon="exclamation-triangle" color="orange" />
                    </ButtonNoStyle>
                  </Tooltip>
                )}
                {props.lefIcon}
              </>
            )
          }
          iconsRight={
            currentEntity != null ? (
              <ButtonNoStyle
                className={classNames(
                  "icon is-small is-right cursor-pointer",
                  !(props.readOnly || props.disabled) && "has-text-link",
                )}
                onClick={onClose}
              >
                <Fa icon="times" />
              </ButtonNoStyle>
            ) : (
              <ComboboxButton
                as={ButtonNoStyle}
                className={classNames(
                  "icon is-small is-right cursor-pointer",
                  !(props.readOnly || props.disabled) && "has-text-link",
                )}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter" || e.key === " ") {
                    buttonClickedRef.current = true;
                  }
                }}
                onClick={toggleSearch}
              >
                <Fa icon="caret-down" />
              </ComboboxButton>
            )
          }
          {...props.controlProps}
        >
          <ComboboxInput
            autoFocus={props.autoFocus}
            className={classNames(
              "input",
              props.className,
              getSizeClasses(props.size),
              getColorsClasses(props.wviState || ""),
            )}
            name={autocompleteKiller}
            placeholder={props.placeholder}
            autoComplete={autocompleteKiller}
            onClick={(e: React.MouseEvent<HTMLInputElement>) => e.currentTarget.select()}
            style={props.styleInput}
            value={term}
            onFocus={onFocus}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            onContextMenu={props.onContextMenu}
            onDoubleClick={onDoubleClick}
            readOnly={props.readOnly}
            disabled={props.disabled}
            title={props.tooltip}
            data-navigation-row={props["data-navigation-row"]}
            data-navigation-column={props["data-navigation-column"]}
            data-navigation-key={props["data-navigation-key"]}
            displayValue={() => term}
          />
        </Control>
        <ComboboxOptions
          // Réinitialise l'index actif de headlessui quand le premier item change (correspondance exacte déplacée en tête par fetchData).
          // "empty" sert de fallback quand la liste est vide, pour éviter que la key soit undefined.
          key={list[0]?.id ?? "empty"}
          ref={popOverRef as any}
          anchor="bottom start"
          modal={false}
          className="is-paddingless autocomplete-list-container scrollable"
          style={
            { width: "var(--input-width)", "--anchor-max-height": "300px" } as React.CSSProperties
          }
          onMouseDown={() => {
            optionClickedRef.current = true;
          }}
        >
          {isLoading && list.length === 0 && (
            <li style={{ padding: "0.5rem", textAlign: "center", listStyle: "none" }}>
              <span className="icon is-small">
                <i className="fas fa-spinner fa-spin" />
              </span>
            </li>
          )}
          {list.map(
            (el) =>
              el && (
                <ComboboxOption
                  as="li"
                  key={el.id}
                  value={el.labelDetails || el.label}
                  className="autocomplete-list-item-parent"
                  onMouseDown={() => {
                    optionClickedRef.current = true;
                  }}
                >
                  {highlightTerm(el.labelDetails || el.label, term)}
                </ComboboxOption>
              ),
          )}
          <button
            tabIndex={-1}
            className="button is-text is-small is-fullwidth"
            onMouseDown={() => {
              optionClickedRef.current = true;
            }}
            onClick={() => setPage((old) => old + 1)}
          >
            {t("commun_charger_plus")}
          </button>
        </ComboboxOptions>
      </Combobox>
    </>
  );
};

export default Autocomplete;

type ActionAllReducerData =
  | ActionTypeData<"REPLACE", Pojo[]>
  | ActionTypeData<"APPEND", { pojos: Pojo[]; first: number; size: number }>
  | Action<"EMPTY">;

function reducerData(state: Pojo[], action: ActionAllReducerData) {
  switch (action.type) {
    case "REPLACE":
      return action.payload;

    case "APPEND": {
      const { first, size, pojos } = action.payload;
      return produce(state, (draft) => {
        for (
          let index = first, last = first + size, dataIndex = 0;
          index < last;
          index++, dataIndex++
        ) {
          draft[index] = pojos[dataIndex];
        }
      });
    }

    case "EMPTY":
      return [];

    default:
      return state;
  }
}
